/**
 * ImageUploadField — shared dialog-based image control for the patient
 * photo and the ID-card front/back tiles. Mirrors Flutter
 * `upload_image_dialog.dart` (`UploadImageDialog` inside an `InputDialog`):
 * clicking the tile opens a dialog showing the current image (or a camera
 * placeholder), with a Choose/Change button and an optional Remove.
 *
 * The file chooser opens from the dialog's Choose/Change control via a native
 * `<label htmlFor>` → visually-hidden `<input type="file">` (NOT a
 * programmatic `inputRef.click()`), which reliably opens the OS picker.
 *
 * Upload pipeline: file → MULTIPART `useUploadImageFile({ area: 'PATIENT' })`
 * → hosted URL → `onChange`. We use the multipart `/UploadImageFile` endpoint
 * (which returns the hosted URL), NOT the base64 `/UploadImage` endpoint — the
 * latter 200s with an EMPTY body for `area: "PATIENT"`, which silently dropped
 * the photo (Dr. Johnny's clinic, Noah Nazha, 2026-07-27). Only a real hosted
 * URL is ever committed — never a base64 `data:` URL, which the backend's
 * URL-width Photo/IdCard columns truncate and which 500s the whole patient
 * save. PHI-safe — the file bytes never leave the local `onPick` closure.
 */

import { useId, useState } from 'react';
import { toast } from 'sonner';
import { Camera, Trash2 } from 'lucide-react';
import { buttonClasses, Modal } from '@scripsteam/scrips-signal-ds';
import { useUploadImageFile } from '@/services/common/use-upload-image';
import { cn } from '@/lib/utils';

const PATIENT_AREA = 'PATIENT';

export interface ImageUploadFieldProps {
  url: string | null;
  onChange: (next: string | null) => void;
  disabled?: boolean;
  /** Trigger shape: circular avatar (photo) vs rectangular tile (ID card). */
  variant: 'photo' | 'idcard';
  /** Dialog header text, e.g. "Change patient photo". */
  dialogTitle: string;
  /** Empty-state label inside the trigger tile. */
  emptyText: string;
  /** Accessible label / img alt. */
  altText: string;
  /** Show a Remove button in the dialog (photo: yes). */
  allowRemove?: boolean;
}

export function ImageUploadField({
  url,
  onChange,
  disabled = false,
  variant,
  dialogTitle,
  emptyText,
  altText,
  allowRemove = false,
}: ImageUploadFieldProps) {
  const inputId = useId();
  const [open, setOpen] = useState(false);
  const uploadMutation = useUploadImageFile();
  const busy = uploadMutation.isPending;
  const hasImage = !!url?.trim();

  const onPick = async (file: File | undefined) => {
    if (!file) return;
    try {
      // Multipart upload returns the HOSTED blob URL. Send the file bytes
      // directly (no base64 round-trip needed).
      const uploaded = await uploadMutation.mutateAsync({
        blob: file,
        area: PATIENT_AREA,
        filename: file.name || 'photo.jpg',
      });
      // Commit ONLY a real hosted URL — never a base64 data URL. If the upload
      // comes back empty, keep the current image and leave the dialog open so
      // the user can retry, rather than persisting base64 (which truncates the
      // Photo/IdCard column and 500s the whole patient save).
      if (uploaded) {
        onChange(uploaded);
        setOpen(false);
      } else {
        // Multipart normally returns a URL; if it ever comes back empty, tell
        // the user instead of silently doing nothing (and never store base64).
        toast.error('Image upload failed — please try again.');
      }
    } catch {
      // Server-error toast surfaces via the global QueryClient handler.
    }
  };

  const trigger =
    variant === 'photo' ? (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        aria-label={altText}
        className={cn(
          'flex h-20 w-20 flex-col items-center justify-center overflow-hidden rounded-full bg-grey-100 text-text-muted',
          hasImage && 'border border-border-default',
          disabled
            ? 'cursor-not-allowed opacity-50'
            : 'hover:text-brand-primary',
        )}
      >
        {hasImage ? (
          <img src={url ?? undefined} alt={altText} className="h-full w-full object-cover" />
        ) : (
          <>
            <Camera className="size-6" aria-hidden />
            <span className="mt-1 text-size-2xs font-bold uppercase tracking-wide">
              {emptyText}
            </span>
          </>
        )}
      </button>
    ) : (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        aria-label={altText}
        className="idc-tile"
        data-filled={hasImage || undefined}
      >
        {hasImage ? (
          <img src={url ?? undefined} alt={altText} className="h-full w-full object-cover" />
        ) : (
          <>
            <Camera className="size-6" aria-hidden />
            <span>{emptyText}</span>
          </>
        )}
      </button>
    );

  return (
    <>
      {trigger}
      <Modal
      open={open}
      onClose={() => setOpen(false)}
      title={dialogTitle}
      size="md"
    >
          

          {/* Visually-hidden file input; Choose/Change label opens it. */}
          <input
            id={inputId}
            type="file"
            accept="image/*"
            className="sr-only"
            disabled={disabled || busy}
            onChange={(e) => {
              onPick(e.target.files?.[0]);
              e.currentTarget.value = '';
            }}
          />

          <div className="idc-preview">
            {hasImage ? (
              <img
                src={url ?? undefined}
                alt={altText}
                className="idc-preview-img max-h-media-preview"
              />
            ) : (
              <div className="idc-none">
                <Camera className="size-6" />
                <span>No image selected</span>
              </div>
            )}
          </div>

          <div className="idc-actions">
            {allowRemove && hasImage ? (
              <button
                type="button"
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                }}
                disabled={busy}
                className={cn(
                  buttonClasses({ variant: 'tertiary' }),
                  'text-status-error hover:text-status-error',
                )}
              >
                <Trash2 className="mr-1 size-4" /> Remove
              </button>
            ) : null}
            <label
              htmlFor={inputId}
              className={cn(
                buttonClasses(),
                busy ? 'pointer-events-none opacity-50' : 'cursor-pointer',
              )}
            >
              {busy ? 'Uploading…' : hasImage ? 'Change' : 'Choose'}
            </label>
          </div>
        </Modal>
    </>
  );
}
